import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { isFeatureEnabled } from '../../../services/featureEnabledService';

/**
 * Feature flag for the refactored (V2) published shift plan page.
 * Gated via the RnRRCCFacadeService IsFeatureEnabled / AppConfig mechanism
 * (see UTR-REFLEX-1164). Registered as `RCC_PUBLISHED_SHIFT_PLAN_V2` in the
 * facade's FeatureName enum + FeatureGating AppConfig allowlist.
 */
const PUBLISHED_SHIFT_PLAN_V2_FEATURE_NAME = 'RCC_PUBLISHED_SHIFT_PLAN_V2';

// ─── State Interface ────────────────────────────────────────────────────────────

export interface PublishedShiftPlanFeatureState {
  /**
   * Whether the refactored published shift plan page is enabled
   * (from the isFeatureEnabled API). `null` = not yet resolved — callers
   * should wait before choosing which UI to render.
   */
  isPublishedShiftPlanV2Enabled: boolean | null;
}

// ─── Initial State ──────────────────────────────────────────────────────────────

const initialState: PublishedShiftPlanFeatureState = {
  isPublishedShiftPlanV2Enabled: null,
};

// ─── Async Thunks ───────────────────────────────────────────────────────────────

export const checkPublishedShiftPlanV2Enabled = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>(
  'publishedShiftPlan/checkFeatureEnabled',
  async (_, { rejectWithValue }) => {
    try {
      const enabled = await isFeatureEnabled(PUBLISHED_SHIFT_PLAN_V2_FEATURE_NAME);
      return enabled;
    } catch (error: unknown) {
      return rejectWithValue('Failed to check feature flag');
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────────────────────────

const publishedShiftPlanFeatureSlice = createSlice({
  name: 'publishedShiftPlanFeature',
  initialState,
  reducers: {
    /**
     * Directly set the flag without hitting the facade. Used in local dev where
     * getUserName() (and therefore isFeatureEnabled) can't resolve without a Boson
     * parent frame — mirrors AgentChatPanelGated's local-dev short-circuit.
     */
    setPublishedShiftPlanV2Enabled(state, action: PayloadAction<boolean>) {
      state.isPublishedShiftPlanV2Enabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkPublishedShiftPlanV2Enabled.fulfilled, (state, action) => {
        state.isPublishedShiftPlanV2Enabled = action.payload;
      })
      .addCase(checkPublishedShiftPlanV2Enabled.rejected, (state) => {
        // Fail closed — fall back to the existing (V1) page on error.
        state.isPublishedShiftPlanV2Enabled = false;
      });
  },
});

export const { setPublishedShiftPlanV2Enabled } = publishedShiftPlanFeatureSlice.actions;

export default publishedShiftPlanFeatureSlice.reducer;
